import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import dfrApi from '../../services/dfrApi';
import '../../styles/dfr.css';

const STATUS_LABEL = {
  removed: 'Removed',
  awaiting_verification: 'Awaiting Verification',
  requested: 'Suppression Requested',
  reappeared: 'Reappeared',
  in_progress: 'In Progress',
  queued: 'Queued',
  suppressed: 'Suppressed',
  monitoring: 'Monitoring',
  blocked: 'Blocked',
};

function Page({ title, children, actions }) {
  return (
    <div className="dfr-page">
      <header className="main-header">
        <div>
          <h1 className="page-title">{title}</h1>
          <div className="dfr-subtitle"><span>Digital Footprint Reduction</span></div>
        </div>
        {actions ? <div className="header-actions">{actions}</div> : null}
      </header>
      <div className="main-body">{children}</div>
    </div>
  );
}

function useAsync(loader, deps = []) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const reload = async () => {
    setLoading(true);
    setError('');
    try {
      setData(await loader());
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Request failed');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    reload();
  }, deps);
  return { data, error, loading, reload };
}

export function DfrClients() {
  const { data, error, loading, reload } = useAsync(async () => (await dfrApi.clients()).data.clients, []);
  const [form, setForm] = useState({ name: '', engagement_type: '', notes: '' });

  const create = async (e) => {
    e.preventDefault();
    await dfrApi.createClient(form);
    setForm({ name: '', engagement_type: '', notes: '' });
    reload();
  };

  return (
    <Page title="Clients">
      {error && <div className="card" style={{ marginBottom: 16 }}><div className="card-body" style={{ color: '#f87171' }}>{error}</div></div>}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header"><h3 className="card-title">New client</h3></div>
        <div className="card-body">
          <form onSubmit={create} className="dfr-filter-bar" style={{ alignItems: 'flex-end' }}>
            <input required placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <input placeholder="Engagement type" value={form.engagement_type} onChange={(e) => setForm({ ...form, engagement_type: e.target.value })} />
            <input placeholder="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} style={{ minWidth: 220 }} />
            <button type="submit" className="btn btn-primary btn-sm">Create</button>
          </form>
        </div>
      </div>
      <div className="card">
        <div className="card-body">
          {loading ? 'Loading…' : (
            <div className="dfr-table-wrap">
              <table className="dfr-ops-table">
                <thead>
                  <tr><th>Name</th><th>Engagement</th><th>Status</th><th>Subjects</th><th>Cases</th></tr>
                </thead>
                <tbody>
                  {(data || []).map((c) => (
                    <tr key={c.id}>
                      <td style={{ fontWeight: 600 }}>{c.name}</td>
                      <td>{c.engagement_type || '—'}</td>
                      <td>{c.status}</td>
                      <td>{c.subject_count}</td>
                      <td>{c.case_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Page>
  );
}

export function DfrCases() {
  const { data, error, loading, reload } = useAsync(async () => {
    const [cases, clients, subjects] = await Promise.all([
      dfrApi.cases(),
      dfrApi.clients(),
      dfrApi.subjects(),
    ]);
    return {
      cases: cases.data.cases,
      clients: clients.data.clients,
      subjects: subjects.data.subjects,
    };
  }, []);
  const [form, setForm] = useState({
    client_id: '',
    subject_id: '',
    title: '',
    initial_risk: 70,
    current_risk: 70,
    new_subject: '',
    city: '',
    state: '',
    phone_hint: '',
  });
  const [scanBusy, setScanBusy] = useState('');
  const [scanMsg, setScanMsg] = useState('');
  const [creating, setCreating] = useState(false);

  const create = async (e) => {
    e.preventDefault();
    setScanMsg('');
    if (!form.client_id) {
      setScanMsg('Select a client first.');
      return;
    }
    const clientName = (data?.clients || []).find((c) => c.id === form.client_id)?.name || '';
    let subjectId = form.subject_id;
    let subjectName = form.new_subject.trim();
    if (!subjectId && !subjectName) {
      // Common case: client is the person being protected
      subjectName = clientName.trim();
    }
    if (!subjectId && !subjectName) {
      setScanMsg('Enter a subject name (or pick an existing subject).');
      return;
    }
    if (!form.city.trim() || !form.state.trim()) {
      setScanMsg('City and state are required for the exposure scan.');
      return;
    }
    setCreating(true);
    try {
      if (!subjectId) {
        const s = await dfrApi.createSubject({
          client_id: form.client_id,
          display_name: subjectName,
          city: form.city.trim(),
          state: form.state.trim(),
          phone_hint: form.phone_hint.trim(),
        });
        subjectId = s.data.id;
      }
      const created = await dfrApi.createCase({
        client_id: form.client_id,
        subject_id: subjectId,
        title: form.title.trim() || `DFR — ${subjectName || clientName}`,
        initial_risk: Number(form.initial_risk),
        current_risk: Number(form.current_risk || form.initial_risk),
      });
      setScanMsg(
        created.data?.scan
          ? `Case created — exposure scan ${created.data.scan.status} (${created.data.scan.id.slice(0, 8)}…). Open Monitoring to review hits.`
          : 'Case created'
      );
      setForm({
        ...form,
        title: '',
        new_subject: '',
        subject_id: '',
        city: '',
        state: '',
        phone_hint: '',
      });
      reload();
    } catch (err) {
      const detail = err?.response?.data;
      const msg =
        (typeof detail === 'string' && detail) ||
        detail?.error ||
        detail?.detail ||
        (detail && JSON.stringify(detail)) ||
        err.message ||
        'Create failed';
      setScanMsg(String(msg));
    } finally {
      setCreating(false);
    }
  };

  const runScan = async (caseId) => {
    setScanBusy(caseId);
    setScanMsg('');
    try {
      const res = await dfrApi.startScan(caseId);
      setScanMsg(`Scan ${res.data.status}: ${res.data.hits || 0} hits / ${res.data.checked || 0} checked — review under Monitoring`);
      reload();
    } catch (err) {
      setScanMsg(err?.response?.data?.error || err.message || 'Scan failed');
    } finally {
      setScanBusy('');
    }
  };

  return (
    <Page title="Exposure Cases">
      {error && <div className="card" style={{ marginBottom: 16 }}><div className="card-body" style={{ color: '#f87171' }}>{error}</div></div>}
      {scanMsg && <div className="card" style={{ marginBottom: 16 }}><div className="card-body" style={{ color: /failed|required|select|error|enter/i.test(scanMsg) ? '#f87171' : undefined }}>{scanMsg}</div></div>}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header"><h3 className="card-title">Open case</h3></div>
        <div className="card-body">
          <form onSubmit={create} className="dfr-filter-bar">
            <select required value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value, subject_id: '' })}>
              <option value="">Client</option>
              {(data?.clients || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <select value={form.subject_id} onChange={(e) => setForm({ ...form, subject_id: e.target.value, new_subject: e.target.value ? '' : form.new_subject })}>
              <option value="">Person to protect (optional)</option>
              {(data?.subjects || []).filter((s) => s.client_id === form.client_id).map((s) => (
                <option key={s.id} value={s.id}>{s.display_name}{s.city ? ` (${s.city}, ${s.state})` : ''}</option>
              ))}
            </select>
            <input
              placeholder={form.subject_id ? 'Using selected person' : 'Person name (blank = client name)'}
              value={form.new_subject}
              disabled={!!form.subject_id}
              onChange={(e) => setForm({ ...form, new_subject: e.target.value })}
            />
            <input required placeholder="City (required)" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
            <input required placeholder="ST" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} style={{ maxWidth: 80 }} />
            <input placeholder="Phone hint" value={form.phone_hint} onChange={(e) => setForm({ ...form, phone_hint: e.target.value })} />
            <input placeholder="Case title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            <input type="number" min="0" max="100" value={form.initial_risk} onChange={(e) => setForm({ ...form, initial_risk: e.target.value })} title="Initial risk" />
            <button type="submit" className="btn btn-primary btn-sm" disabled={creating}>{creating ? 'Creating…' : 'Create case + scan'}</button>
          </form>
          <p style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 8 }}>
            Client = who hired you. Person = who we scan/remove for. Leave person blank to use the client name. City + state required.
          </p>
        </div>
      </div>
      <div className="card">
        <div className="card-body">
          {loading ? 'Loading…' : (
            <div className="dfr-table-wrap">
              <table className="dfr-ops-table">
                <thead>
                  <tr>
                    <th>Case</th><th>Subject</th><th>Client</th><th>Status</th>
                    <th>Risk</th><th>Reduction</th><th>Next sweep</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.cases || []).map((c) => (
                    <tr key={c.id}>
                      <td style={{ fontWeight: 600 }}>{c.case_number}<div style={{ color: 'var(--text-muted)', fontWeight: 400 }}>{c.title}</div></td>
                      <td>{c.subject_name}</td>
                      <td>{c.client_name}</td>
                      <td>{c.status}</td>
                      <td>{c.initial_risk} → {c.current_risk}</td>
                      <td>{c.reduction_pct}%</td>
                      <td>{c.next_sweep_at || '—'}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <button
                          type="button"
                          className="btn btn-sm btn-primary"
                          disabled={scanBusy === c.id}
                          onClick={() => runScan(c.id)}
                          style={{ marginRight: 6 }}
                        >
                          {scanBusy === c.id ? 'Scanning…' : 'Run scan'}
                        </button>
                        <Link to="/dfr/monitoring" className="btn btn-sm btn-secondary">Results</Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Page>
  );
}

export function DfrQueue() {
  const { data, error, loading, reload } = useAsync(async () => {
    const [ops, cases, brokers, meta] = await Promise.all([
      dfrApi.operations(),
      dfrApi.cases(),
      dfrApi.brokers(),
      dfrApi.meta(),
    ]);
    return {
      operations: ops.data.operations,
      cases: cases.data.cases,
      brokers: brokers.data.brokers,
      statuses: meta.data.removal_statuses,
    };
  }, []);
  const [form, setForm] = useState({ case_id: '', broker_id: '', exposure_summary: '', status: 'queued' });

  const create = async (e) => {
    e.preventDefault();
    await dfrApi.createOperation(form);
    setForm({ ...form, exposure_summary: '' });
    reload();
  };

  const setStatus = async (id, status) => {
    await dfrApi.updateOperation(id, { status });
    reload();
  };

  return (
    <Page title="Broker Queue">
      {error && <div className="card" style={{ marginBottom: 16 }}><div className="card-body" style={{ color: '#f87171' }}>{error}</div></div>}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header"><h3 className="card-title">Queue removal</h3></div>
        <div className="card-body">
          <form onSubmit={create} className="dfr-filter-bar">
            <select required value={form.case_id} onChange={(e) => setForm({ ...form, case_id: e.target.value })}>
              <option value="">Case</option>
              {(data?.cases || []).map((c) => <option key={c.id} value={c.id}>{c.case_number} — {c.subject_name}</option>)}
            </select>
            <select required value={form.broker_id} onChange={(e) => setForm({ ...form, broker_id: e.target.value })}>
              <option value="">Broker</option>
              {(data?.brokers || []).filter((b) => b.is_active).map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
            <input placeholder="Exposure summary" value={form.exposure_summary} onChange={(e) => setForm({ ...form, exposure_summary: e.target.value })} />
            <button type="submit" className="btn btn-primary btn-sm">Add</button>
          </form>
        </div>
      </div>
      <div className="card">
        <div className="card-body">
          {loading ? 'Loading…' : (
            <div className="dfr-table-wrap">
              <table className="dfr-ops-table">
                <thead>
                  <tr><th>Source</th><th>Exposure</th><th>Status</th><th>Analyst</th><th>Advance</th></tr>
                </thead>
                <tbody>
                  {(data?.operations || []).map((o) => (
                    <tr key={o.id}>
                      <td style={{ fontWeight: 600 }}>{o.source}</td>
                      <td>{o.exposure_type}</td>
                      <td>{STATUS_LABEL[o.status] || o.status}</td>
                      <td>{o.analyst || '—'}</td>
                      <td>
                        <select
                          value={o.status}
                          onChange={(e) => setStatus(o.id, e.target.value)}
                          style={{ maxWidth: 200 }}
                        >
                          {(data?.statuses || []).map((s) => (
                            <option key={s.value} value={s.value}>{s.label}</option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Page>
  );
}

export function DfrMonitoring() {
  const { data, error, loading, reload } = useAsync(async () => {
    const [sweeps, cases] = await Promise.all([dfrApi.sweeps(), dfrApi.cases()]);
    return { sweeps: sweeps.data.sweeps, cases: cases.data.cases };
  }, []);
  const [form, setForm] = useState({
    case_id: '',
    brokers_checked: 10,
    new_findings: 0,
    reappeared: 0,
    notes: '',
  });
  const [scanCaseId, setScanCaseId] = useState('');
  const [scan, setScan] = useState(null);
  const [scanFilter, setScanFilter] = useState('unreviewed');
  const [busy, setBusy] = useState(false);
  const [reviewBusy, setReviewBusy] = useState('');

  useEffect(() => {
    if (!scanCaseId && data?.cases?.length) {
      const preferred = data.cases.find((c) => /samantha/i.test(c.subject_name || '')) || data.cases[0];
      if (preferred) setScanCaseId(preferred.id);
    }
  }, [data, scanCaseId]);

  useEffect(() => {
    if (!scanCaseId) {
      setScan(null);
      return undefined;
    }
    let cancelled = false;
    let timer;
    const load = async () => {
      try {
        const detail = await dfrApi.case(scanCaseId);
        if (cancelled) return;
        const latest = detail.data.latest_scan;
        setScan(latest);
        if (latest && (latest.status === 'queued' || latest.status === 'running')) {
          timer = setTimeout(load, 2500);
        }
      } catch {
        if (!cancelled) setScan(null);
      }
    };
    load();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [scanCaseId]);

  const create = async (e) => {
    e.preventDefault();
    await dfrApi.createSweep(form);
    setForm({ ...form, notes: '', new_findings: 0, reappeared: 0 });
    reload();
  };

  const startScan = async () => {
    if (!scanCaseId) return;
    setBusy(true);
    try {
      const res = await dfrApi.startScan(scanCaseId);
      setScan(res.data);
      reload();
    } finally {
      setBusy(false);
    }
  };

  const refreshScan = async () => {
    if (!scan?.id) return;
    const res = await dfrApi.scan(scan.id);
    setScan(res.data);
  };

  const review = async (resultId, decision) => {
    setReviewBusy(resultId);
    try {
      await dfrApi.reviewScanResult(resultId, { decision, queue_removal: decision === 'confirmed' });
      await refreshScan();
      reload();
    } finally {
      setReviewBusy('');
    }
  };

  const results = (scan?.results || []).filter((r) => {
    if (scanFilter === 'all') return true;
    if (scanFilter === 'unreviewed') return r.status === 'hit' && !r.review_decision;
    return r.status === scanFilter;
  });

  return (
    <Page title="Monitoring">
      {error && <div className="card" style={{ marginBottom: 16 }}><div className="card-body" style={{ color: '#f87171' }}>{error}</div></div>}

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <h3 className="card-title">Exposure scan results</h3>
          <div className="dfr-filter-bar" style={{ margin: 0 }}>
            <select value={scanCaseId} onChange={(e) => setScanCaseId(e.target.value)}>
              <option value="">Select case</option>
              {(data?.cases || []).map((c) => (
                <option key={c.id} value={c.id}>{c.case_number} — {c.subject_name}</option>
              ))}
            </select>
            <button type="button" className="btn btn-primary btn-sm" disabled={!scanCaseId || busy} onClick={startScan}>
              {busy ? 'Starting…' : 'Run exposure scan'}
            </button>
            <button type="button" className="btn btn-secondary btn-sm" disabled={!scan?.id} onClick={refreshScan}>Refresh</button>
          </div>
        </div>
        <div className="card-body">
          {!scanCaseId && <p style={{ color: 'var(--text-muted)', margin: 0 }}>Select her case (e.g. Samantha Eller), then work the unreviewed hits below.</p>}
          {scanCaseId && !scan && <p style={{ color: 'var(--text-muted)', margin: 0 }}>No scans yet for this case.</p>}
          {scan && (
            <>
              <div style={{ marginBottom: 12, padding: '10px 12px', border: '1px solid rgba(148,163,184,0.25)', borderRadius: 8, background: 'rgba(14,18,28,0.55)', fontSize: 13, lineHeight: 1.45 }}>
                <strong>How to take down:</strong> open <em>Investigate</em> and verify it is actually her (snippets can be wrong relatives/namesakes).
                Then <strong>Queue takedown</strong> to create an exposure + Broker Queue item, or <strong>Not her</strong> to dismiss.
                Work the queue under <strong>DFR → Broker Queue</strong>.
              </div>
              <p style={{ marginTop: 0 }}>
                Status: <strong>{scan.status}</strong>
                {' · '}checked {scan.checked}
                {' · '}hits {scan.hits}
                {' · '}blocked/manual {scan.blocked}
                {' · '}clear {scan.clear}
                {' · '}errors {scan.errors}
                {' · '}unreviewed {(scan.results || []).filter((r) => r.status === 'hit' && !r.review_decision).length}
              </p>
              <div className="dfr-filter-bar">
                {['unreviewed', 'hit', 'manual_needed', 'blocked', 'clear', 'error', 'all'].map((f) => (
                  <button
                    key={f}
                    type="button"
                    className={`btn btn-sm ${scanFilter === f ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => setScanFilter(f)}
                  >
                    {f}
                  </button>
                ))}
              </div>
              <div className="dfr-table-wrap" style={{ marginTop: 12 }}>
                <table className="dfr-ops-table">
                  <thead>
                    <tr>
                      <th>Broker</th><th>Cluster</th><th>What we found</th><th>Investigate</th><th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((r) => {
                      const google = r.raw?.google_search || (r.raw?.query ? `https://www.google.com/search?q=${encodeURIComponent(r.raw.query)}` : '');
                      const investigate = (r.evidence_url && /^https?:\/\//i.test(r.evidence_url) && !r.evidence_url.includes('/goto?'))
                        ? r.evidence_url
                        : (google || r.evidence_url);
                      return (
                      <tr key={r.id}>
                        <td style={{ fontWeight: 600 }}>{r.broker_name}<div style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: 11 }}>{r.cluster || 'independent'} · {r.confidence}</div></td>
                        <td>{r.status}{r.review_decision ? ` / ${r.review_decision}` : ''}</td>
                        <td style={{ maxWidth: 420 }}>{r.snippet || '—'}{r.raw?.query ? <div style={{ color: 'var(--text-muted)', fontSize: 11, marginTop: 4 }}>Query: {r.raw.query}</div> : null}</td>
                        <td>
                          {investigate
                            ? <a href={investigate} target="_blank" rel="noreferrer">Investigate</a>
                            : '—'}
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {r.status === 'hit' && !r.review_decision ? (
                            <>
                              <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                disabled={reviewBusy === r.id}
                                onClick={() => review(r.id, 'confirmed')}
                                style={{ marginRight: 4 }}
                              >
                                Queue takedown
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-secondary"
                                disabled={reviewBusy === r.id}
                                onClick={() => review(r.id, 'false_positive')}
                              >
                                Not her
                              </button>
                            </>
                          ) : (r.review_decision === 'confirmed' ? 'Queued' : (r.review_decision || '—'))}
                        </td>
                      </tr>
                      );
                    })}
                    {!results.length && (
                      <tr><td colSpan={5} style={{ color: 'var(--text-muted)' }}>No results for this filter. Try &quot;hit&quot; or &quot;all&quot;.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header"><h3 className="card-title">Record sweep</h3></div>
        <div className="card-body">
          <form onSubmit={create} className="dfr-filter-bar">
            <select required value={form.case_id} onChange={(e) => setForm({ ...form, case_id: e.target.value })}>
              <option value="">Case</option>
              {(data?.cases || []).map((c) => <option key={c.id} value={c.id}>{c.case_number} — {c.subject_name}</option>)}
            </select>
            <input type="number" min="0" title="Brokers checked" value={form.brokers_checked} onChange={(e) => setForm({ ...form, brokers_checked: e.target.value })} />
            <input type="number" min="0" title="New findings" value={form.new_findings} onChange={(e) => setForm({ ...form, new_findings: e.target.value })} />
            <input type="number" min="0" title="Reappeared" value={form.reappeared} onChange={(e) => setForm({ ...form, reappeared: e.target.value })} />
            <input placeholder="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            <button type="submit" className="btn btn-primary btn-sm">Save sweep</button>
          </form>
          <p style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 8 }}>
            Automated scans also write a sweep row when they finish.
          </p>
        </div>
      </div>
      <div className="card">
        <div className="card-body">
          {loading ? 'Loading…' : (
            <div className="dfr-table-wrap">
              <table className="dfr-ops-table">
                <thead>
                  <tr><th>Date</th><th>Case</th><th>Checked</th><th>New</th><th>Reappeared</th><th>By</th><th>Notes</th></tr>
                </thead>
                <tbody>
                  {(data?.sweeps || []).map((s) => (
                    <tr key={s.id}>
                      <td>{s.swept_at}</td>
                      <td>{s.case_id.slice(0, 8)}…</td>
                      <td>{s.brokers_checked}</td>
                      <td>{s.new_findings}</td>
                      <td>{s.reappeared}</td>
                      <td>{s.performed_by || '—'}</td>
                      <td>{s.notes || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Page>
  );
}

export function DfrEvidence() {
  const { data, error, loading, reload } = useAsync(async () => {
    const [evidence, cases, ops] = await Promise.all([
      dfrApi.evidence(),
      dfrApi.cases(),
      dfrApi.operations(),
    ]);
    return {
      evidence: evidence.data.evidence,
      cases: cases.data.cases,
      operations: ops.data.operations,
    };
  }, []);
  const [form, setForm] = useState({ case_id: '', removal_op_id: '', title: '', file: null });

  const create = async (e) => {
    e.preventDefault();
    const fd = new FormData();
    fd.append('case_id', form.case_id);
    if (form.removal_op_id) fd.append('removal_op_id', form.removal_op_id);
    fd.append('title', form.title || 'Evidence');
    if (form.file) fd.append('file', form.file);
    await dfrApi.uploadEvidence(fd);
    setForm({ ...form, title: '', file: null });
    reload();
  };

  return (
    <Page title="DFR Evidence">
      {error && <div className="card" style={{ marginBottom: 16 }}><div className="card-body" style={{ color: '#f87171' }}>{error}</div></div>}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header"><h3 className="card-title">Upload evidence</h3></div>
        <div className="card-body">
          <form onSubmit={create} className="dfr-filter-bar">
            <select required value={form.case_id} onChange={(e) => setForm({ ...form, case_id: e.target.value })}>
              <option value="">Case</option>
              {(data?.cases || []).map((c) => <option key={c.id} value={c.id}>{c.case_number} — {c.subject_name}</option>)}
            </select>
            <select value={form.removal_op_id} onChange={(e) => setForm({ ...form, removal_op_id: e.target.value })}>
              <option value="">Optional op</option>
              {(data?.operations || []).filter((o) => o.case_id === form.case_id).map((o) => (
                <option key={o.id} value={o.id}>{o.source}</option>
              ))}
            </select>
            <input placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            <input type="file" onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })} />
            <button type="submit" className="btn btn-primary btn-sm">Upload</button>
          </form>
        </div>
      </div>
      <div className="card">
        <div className="card-body">
          {loading ? 'Loading…' : (
            <div className="dfr-table-wrap">
              <table className="dfr-ops-table">
                <thead>
                  <tr><th>Title</th><th>File</th><th>Case</th><th>By</th><th>When</th></tr>
                </thead>
                <tbody>
                  {(data?.evidence || []).map((ev) => (
                    <tr key={ev.id}>
                      <td style={{ fontWeight: 600 }}>{ev.title}</td>
                      <td>{ev.url ? <a href={ev.url} target="_blank" rel="noreferrer">{ev.filename || 'Download'}</a> : '—'}</td>
                      <td>{ev.case_id.slice(0, 8)}…</td>
                      <td>{ev.uploaded_by || '—'}</td>
                      <td>{ev.created_at?.slice(0, 10)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Page>
  );
}

export function DfrReports() {
  const { data, error, loading, reload } = useAsync(async () => {
    const [reports, cases] = await Promise.all([dfrApi.reports(), dfrApi.cases()]);
    return { reports: reports.data.reports, cases: cases.data.cases };
  }, []);
  const [caseId, setCaseId] = useState('');

  const generate = async () => {
    if (!caseId) return;
    const res = await dfrApi.createReport({ case_id: caseId });
    const w = window.open('', '_blank');
    if (w) {
      w.document.write(res.data.html || '');
      w.document.close();
    }
    reload();
  };

  const openReport = async (id) => {
    const res = await dfrApi.report(id);
    const w = window.open('', '_blank');
    if (w) {
      w.document.write(res.data.html || '');
      w.document.close();
    }
  };

  return (
    <Page
      title="DFR Reports"
      actions={(
        <>
          <select className="dfr-client-select" value={caseId} onChange={(e) => setCaseId(e.target.value)}>
            <option value="">Select case</option>
            {(data?.cases || []).map((c) => <option key={c.id} value={c.id}>{c.case_number} — {c.subject_name}</option>)}
          </select>
          <button type="button" className="btn btn-primary btn-sm" onClick={generate} disabled={!caseId}>Generate</button>
        </>
      )}
    >
      {error && <div className="card" style={{ marginBottom: 16 }}><div className="card-body" style={{ color: '#f87171' }}>{error}</div></div>}
      <div className="card">
        <div className="card-body">
          {loading ? 'Loading…' : (
            <div className="dfr-table-wrap">
              <table className="dfr-ops-table">
                <thead>
                  <tr><th>Title</th><th>By</th><th>Created</th><th></th></tr>
                </thead>
                <tbody>
                  {(data?.reports || []).map((r) => (
                    <tr key={r.id}>
                      <td style={{ fontWeight: 600 }}>{r.title}</td>
                      <td>{r.created_by || '—'}</td>
                      <td>{r.created_at?.slice(0, 19).replace('T', ' ')}</td>
                      <td><button type="button" className="btn btn-sm btn-secondary" onClick={() => openReport(r.id)}>Open</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Page>
  );
}

export function DfrSettings() {
  const { data, error, loading, reload } = useAsync(async () => (await dfrApi.brokers()).data.brokers, []);
  const [form, setForm] = useState({ name: '', website: '', removal_url: '', removal_template: '' });
  const [filter, setFilter] = useState('');
  const [clusterOnly, setClusterOnly] = useState(false);

  const create = async (e) => {
    e.preventDefault();
    await dfrApi.createBroker(form);
    setForm({ name: '', website: '', removal_url: '', removal_template: '' });
    reload();
  };

  const q = filter.trim().toLowerCase();
  const rows = (data || []).filter((b) => {
    if (clusterOnly && !b.parent_name && !b.cluster) return false;
    if (!q) return true;
    return (
      b.name.toLowerCase().includes(q) ||
      (b.website || '').toLowerCase().includes(q) ||
      (b.removal_url || '').toLowerCase().includes(q) ||
      (b.parent_name || '').toLowerCase().includes(q) ||
      (b.cluster || '').toLowerCase().includes(q)
    );
  });

  const mapped = (data || []).filter((b) => b.parent_name).length;

  return (
    <Page title="DFR Settings">
      {error && <div className="card" style={{ marginBottom: 16 }}><div className="card-body" style={{ color: '#f87171' }}>{error}</div></div>}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header"><h3 className="card-title">Add broker</h3></div>
        <div className="card-body">
          <form onSubmit={create} className="dfr-filter-bar">
            <input required placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <input placeholder="Website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} />
            <input placeholder="Removal URL" value={form.removal_url} onChange={(e) => setForm({ ...form, removal_url: e.target.value })} />
            <button type="submit" className="btn btn-primary btn-sm">Add</button>
          </form>
        </div>
      </div>
      <div className="card">
        <div className="card-header" style={{ justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <h3 className="card-title">
            Broker catalog{!loading && data ? ` (${data.length})` : ''}
            {!loading && data ? <span className="dfr-muted"> · {mapped} mapped to parent</span> : null}
          </h3>
          <div className="dfr-filter-bar" style={{ margin: 0 }}>
            {!loading && data ? (
              <>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-muted, #94a3b8)' }}>
                  <input type="checkbox" checked={clusterOnly} onChange={(e) => setClusterOnly(e.target.checked)} />
                  Clustered only
                </label>
                <input
                  placeholder="Filter brokers / parents…"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  style={{ maxWidth: 260 }}
                />
              </>
            ) : null}
          </div>
        </div>
        <div className="card-body">
          {loading ? 'Loading…' : (
            <div className="dfr-table-wrap">
              <table className="dfr-ops-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Cluster</th>
                    <th>Parent / feed</th>
                    <th>Website</th>
                    <th>Removal</th>
                    <th>Active</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((b) => {
                    const optUrl = b.effective_removal_url || b.removal_url;
                    const norm = (u) => String(u || '').replace(/\/$/, '');
                    const viaParent = !!(
                      b.parent_name &&
                      optUrl &&
                      b.parent_removal_url &&
                      norm(optUrl) === norm(b.parent_removal_url)
                    );
                    return (
                      <tr key={b.id}>
                        <td style={{ fontWeight: 600 }}>{b.name}</td>
                        <td>{b.cluster || '—'}</td>
                        <td>{b.parent_name || (b.cluster ? 'Hub' : '—')}</td>
                        <td>{b.website ? <a href={b.website} target="_blank" rel="noreferrer">Link</a> : '—'}</td>
                        <td>
                          {optUrl ? (
                            <a href={optUrl} target="_blank" rel="noreferrer" title={optUrl}>
                              {viaParent ? `Opt-out via ${b.parent_name}` : 'Opt-out'}
                            </a>
                          ) : '—'}
                        </td>
                        <td>{b.is_active ? 'Yes' : 'No'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Page>
  );
}
export default DfrClients;
